import { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function DeviationModal({ checklistId, onClose, onSaved }) {
  const [checklists, setChecklists] = useState([]);
  const [selectedChecklist, setSelectedChecklist] = useState(checklistId || "");
  const [type, setType] = useState("Enstaka händelse");
  const [description, setDescription] = useState("");
  const [newRisk, setNewRisk] = useState("ja");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/checklists").then((r) => setChecklists(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    if (!description.trim()) {
      toast.error("Beskrivning av bristen krävs");
      return;
    }
    setSaving(true);
    try {
      await api.post("/deviations", {
        checklist_id: selectedChecklist || null,
        type,
        description,
        requires_risk_update: newRisk === "ja",
      });
      toast.success("Avvikelsen har rapporterats och kvalitetsansvarig notifieras");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="deviation-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 space-y-4 border border-slate-200">
        <div className="flex justify-between items-center border-b border-slate-100 pb-3">
          <h3 className="font-bold text-base flex items-center text-red-600">
            <AlertTriangle className="w-4 h-4 mr-2" />Rapportera Avvikelse / Kvalitetsbrist
          </h3>
          <button data-testid="deviation-modal-close" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-3 text-xs">
          <div>
            <label className="block font-semibold text-slate-700 mb-1">Kopplat uppdrag (valfritt):</label>
            <select
              data-testid="deviation-checklist-select"
              className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-blue-500"
              value={selectedChecklist} onChange={(e) => setSelectedChecklist(e.target.value)}
            >
              <option value="">— Ingen koppling / generell händelse —</option>
              {checklists.map((c) => (
                <option key={c.id} value={c.id}>{c.client_name} — {c.engagement_type}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-semibold text-slate-700 mb-1">* Typ av brist (Reko p. 2):</label>
            <select
              data-testid="deviation-type-select"
              className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-blue-500"
              value={type} onChange={(e) => setType(e.target.value)}
            >
              <option value="Enstaka händelse">Enstaka händelse (t.ex. sjukdom, enskild miss)</option>
              <option value="Generell/Systemisk">Generell / Återkommande brist (kräver rutinsystemsändring)</option>
            </select>
          </div>
          <div>
            <label className="block font-semibold text-slate-700 mb-1">* Beskrivning av bristen (Vad har hänt & varför?):</label>
            <textarea
              data-testid="deviation-description-input"
              className="w-full p-2 border border-slate-300 rounded h-20"
              placeholder="Skriv detaljer om bristen..."
              value={description} onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div>
            <label className="block font-semibold text-slate-700 mb-1">* Innebär detta en ny risk som kräver nya fasta åtgärder framöver?</label>
            <div className="space-y-1">
              <label className="flex items-center space-x-2">
                <input data-testid="deviation-newrisk-yes" type="radio" name="modalNewRisk" checked={newRisk === "ja"} onChange={() => setNewRisk("ja")} />
                <span>Ja, flagga för uppdatering av riskanalys (Modul 2)</span>
              </label>
              <label className="flex items-center space-x-2">
                <input data-testid="deviation-newrisk-no" type="radio" name="modalNewRisk" checked={newRisk === "nej"} onChange={() => setNewRisk("nej")} />
                <span>Nej, befintliga åtgärder är tillräckliga</span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 bg-slate-100 text-slate-700 text-xs font-semibold rounded hover:bg-slate-200">Avbryt</button>
          <button
            data-testid="deviation-save-btn" onClick={save} disabled={saving}
            className="px-4 py-2 bg-red-600 text-white text-xs font-bold rounded hover:bg-red-700 shadow disabled:opacity-60"
          >
            {saving ? "Sparar..." : "Spara & Notifiera"}
          </button>
        </div>
      </div>
    </div>
  );
}
